/**
 * Training availability for a client persona (admin persona library, 6 Oct 2026).
 * Personas built for advanced simulation stay out of training until an admin shares
 * them here: with every training group, or with chosen groups. Separate from publish
 * status, which no longer decides what lecturers can pick.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, RadioGroup, FormControlLabel, Radio, FormControl, InputLabel, Select,
  MenuItem, Chip, OutlinedInput, Button, Alert
} from '@mui/material';
import { getTrainingGroupsForSharing, setPersonaTrainingSharing } from '../../../services/personaLibraryApi';

const PersonaTrainingSharing = ({ persona, token, onChanged }) => {
  const current = persona?.trainingSharing || {};
  const [mode, setMode] = useState(current.mode || 'off');
  const [groupIds, setGroupIds] = useState((current.groupIds || []).map(String));
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setMode(current.mode || 'off');
    setGroupIds((current.groupIds || []).map(String));
    setMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona?.key]);

  useEffect(() => {
    let cancelled = false;
    getTrainingGroupsForSharing(token)
      .then(data => { if (!cancelled) setGroups(data.groups || []); })
      .catch(() => { if (!cancelled) setGroups([]); });
    return () => { cancelled = true; };
  }, [token]);

  const unchanged = mode === (current.mode || 'off')
    && (mode !== 'groups' || JSON.stringify([...groupIds].sort()) === JSON.stringify((current.groupIds || []).map(String).sort()));

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const data = await setPersonaTrainingSharing(persona.key, { mode, groupIds: mode === 'groups' ? groupIds : [] }, token);
      setMessage({ severity: 'success', text: 'Saved.' });
      if (onChanged) onChanged(data.trainingSharing);
    } catch (err) {
      setMessage({ severity: 'error', text: err?.msg || err?.message || 'Could not save' });
    } finally {
      setSaving(false);
    }
  };

  const nameOf = (id) => groups.find(g => String(g._id) === id)?.name || id;

  return (
    <Box sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Available for training</Typography>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
        Lecturers can choose this persona for their scenarios. Advanced simulation is not affected.
      </Typography>
      <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
        <FormControlLabel value="off" control={<Radio size="small" />} label="Not shared with training" />
        <FormControlLabel value="all" control={<Radio size="small" />} label="All training groups" />
        <FormControlLabel value="groups" control={<Radio size="small" />} label="Chosen training groups" />
      </RadioGroup>
      {mode === 'groups' && (
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel id="persona-training-groups-label">Training groups</InputLabel>
          <Select
            labelId="persona-training-groups-label"
            id="persona-training-groups"
            multiple
            value={groupIds}
            onChange={(e) => setGroupIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            input={<OutlinedInput label="Training groups" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map(id => <Chip key={id} size="small" label={nameOf(id)} />)}
              </Box>
            )}
          >
            {groups.map(g => <MenuItem key={g._id} value={String(g._id)}>{g.name}</MenuItem>)}
          </Select>
        </FormControl>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving || unchanged || (mode === 'groups' && !groupIds.length)}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
        {message && <Alert severity={message.severity} sx={{ py: 0 }}>{message.text}</Alert>}
      </Box>
    </Box>
  );
};

export const trainingSharingLabel = (persona) => {
  const s = persona?.trainingSharing;
  if (s?.mode === 'all') return 'Training: all groups';
  if (s?.mode === 'groups') return `Training: ${(s.groupIds || []).length} group${(s.groupIds || []).length === 1 ? '' : 's'}`;
  return null;
};

export default PersonaTrainingSharing;
